import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    FileText, PlayCircle, Clock, Settings,
} from 'lucide-react';
import {
    PageHeader, StatsGrid, ListHeader, Pagination,
    FilterGroup, FilterSelect, SearchBar, DateRangeFilter,
    Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/shared/ui';
import { Scenario } from '../../types/admin';
import { useScenarioAdminList } from '@/hooks/useScenario';
import { getScenarioCategoryList, getDeviceTypeList } from '@/services/scenarioService';
import {
    convertScenarioAdminDtoToScenario,
    extractScenarioListCategoryRows,
    extractScenarioListDeviceTypeRows,
    resolveDeviceTypeIdForScenarioListPlatform,
    type ScenarioListPlatformFilterKey,
} from '@/utils/scenarioUtils';
import { formatLocaleInteger } from '@/lib/localeFormat';
export interface MasterScenarioManagementProps {
    /** OrgPage 등에 임베드 시: 시나리오 제목 클릭 시 이동 대신 콜백 호출 (레이아웃 유지) */
    onScenarioClick?: (scenarioId: string | number) => void;
    /** true면 상단 PageHeader(타이틀) 숨김 - 상위에서 동일 타이틀 사용 시 */
    embedMode?: boolean;
}

export default function MasterScenarioManagement(props?: MasterScenarioManagementProps): React.ReactElement {
    const { onScenarioClick, embedMode = false } = props ?? {};
    const { t, i18n } = useTranslation('scenario');
    const navigate = useNavigate();
    const basePath = '/master';

    const [scenarios, setScenarios] = useState<Scenario[]>([]);
    const [filteredScenarios, setFilteredScenarios] = useState<Scenario[]>([]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState<number>(1);
    const itemsPerPage = 10;

    // Filter states
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [platformFilter, setPlatformFilter] = useState<ScenarioListPlatformFilterKey | 'all'>('all');
    const [categoryRows, setCategoryRows] = useState<{ id: number; title: string }[]>([]);
    const [deviceRows, setDeviceRows] = useState<{ id: number; title: string }[]>([]);
    const [dateRange, setDateRange] = useState<{ start: string | null; end: string | null }>({ start: null, end: null });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [catRes, devRes] = await Promise.all([
                    getScenarioCategoryList(undefined, i18n.language),
                    getDeviceTypeList(),
                ]);
                if (cancelled) return;
                setCategoryRows(extractScenarioListCategoryRows(catRes));
                setDeviceRows(extractScenarioListDeviceTypeRows(devRes));
            } catch {
                if (!cancelled) {
                    setCategoryRows([]);
                    setDeviceRows([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const platformDeviceTypeIdForApi = useMemo(() => {
        if (platformFilter === 'all') return undefined;
        const id = resolveDeviceTypeIdForScenarioListPlatform(deviceRows, platformFilter);
        return id != null ? String(id) : undefined;
    }, [deviceRows, platformFilter]);

    // 실 API: GET /scenario/list/option 기반
    const { data: apiData, isLoading, error } = useScenarioAdminList(
        {
            page: currentPage,
            pageSize: itemsPerPage,
            search: searchQuery || undefined,
            startDate: dateRange.start || undefined,
            endDate: dateRange.end || undefined,
            category: categoryFilter !== 'all' ? categoryFilter : undefined,
            platform: platformDeviceTypeIdForApi,
        },
        { staleTime: 0, refetchOnMount: true }
    );

    // API 데이터 → Scenario 변환
    useEffect(() => {
        if (apiData != null) {
            const list = apiData.scenarioList ?? [];
            setScenarios(list.map(convertScenarioAdminDtoToScenario));
            return;
        }
        if (error && !isLoading) {
            setScenarios([]);
        }
    }, [apiData, error, isLoading]);

    // 총 시나리오 수 (서버 totalCount 우선)
    const totalCount = apiData?.totalCount ?? scenarios.length;

    // Derived Stats
    const stats = useMemo(() => {
        const totalSimulations = scenarios.reduce((acc, s) => acc + (s.simulationCount || 0), 0);

        const calculateTotalSimulationTime = (): string => {
            let totalMinutes = 0;
            scenarios.forEach((scenario) => {
                const duration = scenario.duration || '0분';
                const hourMatch = duration.match(/(\d+)시간/);
                const minuteMatch = duration.match(/(\d+)분/);
                const secondMatch = duration.match(/(\d+)초/);
                if (hourMatch) totalMinutes += parseInt(hourMatch[1]) * 60;
                if (minuteMatch) totalMinutes += parseInt(minuteMatch[1]);
                if (secondMatch && !minuteMatch && !hourMatch) {
                    totalMinutes += Math.ceil(parseInt(secondMatch[1]) / 60);
                }
            });
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            return hours > 0 ? `${hours}시간 ${minutes}분` : `${minutes}분`;
        };

        return [
            {
                id: 'totalScenarios',
                label: t('management.statsTotal'),
                value: formatLocaleInteger(totalCount, i18n.language),
                sub: t('management.count'),
                icon: FileText,
                color: 'brand',
                isActive: false,
                onClick: () => {},
            },
            {
                id: 'totalSimulations',
                label: t('management.statsSimulations'),
                value: formatLocaleInteger(totalSimulations, i18n.language),
                sub: t('management.times'),
                icon: PlayCircle,
                color: 'brand',
                isActive: false,
                onClick: () => {},
            },
            {
                id: 'totalSimulationTime',
                label: t('management.statsSimulationTime'),
                value: calculateTotalSimulationTime(),
                sub: '',
                icon: Clock,
                color: 'brand',
                isActive: false,
                onClick: () => {},
            },
            {
                id: 'customScenarios',
                label: t('management.statsCustom'),
                value: '—',
                sub: t('management.count'),
                icon: Settings,
                color: 'brand',
                isActive: false,
                onClick: () => {},
            },
        ];
    }, [i18n.language, scenarios, totalCount, t]);

    useEffect(() => {
        setFilteredScenarios(scenarios);
    }, [scenarios]);

    // 필터 변경 시 1페이지로 리셋
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, categoryFilter, platformFilter, dateRange.start, dateRange.end]);

    // 서버 페이지네이션: API가 현재 페이지만 반환하므로 filteredScenarios를 그대로 사용
    const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
    const paginatedItems = filteredScenarios;

    const categoryOptions = useMemo(
        () => [
            { value: 'all', label: t('management.filterCategoryAll') },
            ...categoryRows.map((c) => ({ value: String(c.id), label: c.title })),
        ],
        [categoryRows, t]
    );

    const platformOptions = useMemo(
        () => [
            { value: 'all', label: t('management.filterPlatformAll') },
            { value: 'VR', label: 'VR' },
            { value: 'MOBILE', label: 'Mobile' },
            { value: 'PC', label: 'PC' },
        ],
        [t]
    );

    const onPlatformFilterChange = useCallback((val: string) => {
        if (val === 'all' || val === 'VR' || val === 'MOBILE' || val === 'PC') {
            setPlatformFilter(val);
        }
    }, []);

    const formatPlatform = (platformString: unknown) => {
        const s = String(platformString || '');
        if (!s || s === '-') return '-';
        return s
            .split(',')
            .map((p) => p.trim().toUpperCase())
            .map((p) => (p === 'MOBILE' ? 'Mobile' : p))
            .join(', ');
    };

    return (
        <>
            {!embedMode && (
                <PageHeader
                    title={t('management.title')}
                    breadcrumbs={[
                        { label: t('management.title') }
                    ]}
                    actions={
                        <div className="flex items-center gap-3">
                            <DateRangeFilter
                                startDate={dateRange.start || ''}
                                endDate={dateRange.end || ''}
                                onStartDateChange={(val) => setDateRange(prev => ({ ...prev, start: val }))}
                                onEndDateChange={(val) => setDateRange(prev => ({ ...prev, end: val }))}
                            />
                        </div>
                    }
                />
            )}

            {embedMode && (
                <div className="flex items-center gap-3 mb-4">
                    <DateRangeFilter
                        startDate={dateRange.start || ''}
                        endDate={dateRange.end || ''}
                        onStartDateChange={(val) => setDateRange(prev => ({ ...prev, start: val }))}
                        onEndDateChange={(val) => setDateRange(prev => ({ ...prev, end: val }))}
                    />
                </div>
            )}

            {/* Stats Section */}
            <div className="mb-6">
                <StatsGrid items={stats} />
            </div>

            {/* 로딩 상태 */}
            {isLoading && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">{t('management.loading')}</p>
                </div>
            )}

            {/* 에러 상태 */}
            {error != null && !isLoading && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
                    <p className="text-sm font-medium text-red-800">시나리오 목록을 불러오지 못했습니다.</p>
                    <p className="text-sm text-red-700 mt-1">
                        {error instanceof Error ? error.message : String(error)}
                    </p>
                </div>
            )}

            {/* List Header & Filters */}
            <ListHeader
                totalCount={totalCount}
                rightContent={
                    <FilterGroup>
                        <FilterSelect
                            options={categoryOptions}
                            value={categoryFilter}
                            onValueChange={(val) => setCategoryFilter(val)}
                        />
                        <FilterSelect
                            options={platformOptions}
                            value={platformFilter}
                            onValueChange={onPlatformFilterChange}
                        />
                        <SearchBar
                            placeholder={t('management.searchPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </FilterGroup>
                }
            />

            {/* Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50/50">
                            <TableHead className="w-[60px]">No.</TableHead>
                            <TableHead>{t('management.tableCategory')}</TableHead>
                            <TableHead>{t('management.tableTitle')}</TableHead>
                            <TableHead>{t('management.tablePlatform')}</TableHead>
                            <TableHead className="text-right">{t('management.tableSimulations')}</TableHead>
                            <TableHead>{t('management.tableRegisteredAt')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedItems.map((item, index) => {
                            const rowNo = (currentPage - 1) * itemsPerPage + index + 1;
                            return (
                                <TableRow key={item.id} className="hover:bg-gray-50 transition-colors cursor-pointer text-base">
                                    <TableCell>{rowNo}</TableCell>
                                    <TableCell>
                                        <span className="text-gray-900">{item.category}</span>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <div
                                                className="font-semibold text-blue-600 hover:text-blue-800 cursor-pointer"
                                                onClick={() =>
                                                    onScenarioClick
                                                        ? onScenarioClick(item.id)
                                                        : navigate(`${basePath}/scenarios/${item.id}`)
                                                }
                                            >
                                                {item.title}
                                            </div>
                                            {item.modeValueTitle && (
                                                <span className="inline-flex items-center px-[3px] py-[1px] rounded text-[11px] font-medium bg-sky-50 text-sky-700 border border-sky-300 leading-tight">
                                                    {item.modeValueTitle}
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>{formatPlatform(item.platform)}</TableCell>
                                    <TableCell className="text-right">
                                        {formatLocaleInteger(item.simulationCount ?? 0, i18n.language)}
                                    </TableCell>
                                    <TableCell>{item.createdDate || '-'}</TableCell>
                                </TableRow>
                            );
                        })}
                        {paginatedItems.length === 0 && !isLoading && (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                                    데이터가 없습니다.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalCount}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
            />
        </>
    );
}
